import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/react';
import './index.scss';
import App from './App';
import reportWebVitals from './reportWebVitals';
import AppCrashFallback from './components/AppCrashFallback';
import { scheduleBootLoaderRemoval } from './bootLoaderTeardown';
import { buildSentryOptions } from './utils/sentryConfig';
import { INK_TOKEN_SWEEP_ENABLED } from './constants/featureFlags';

// INK-TOKEN-SWEEP (audit 2026-07-13 Y1 / DL-021): when the flag is ON, add the
// `ink-token-sweep` class to <body> so the inert `body.ink-token-sweep { color:
// $blue }` rule in index.scss activates, retokening the base text color from
// Bootstrap ink #212529 to brand ink #123051 at the shared root. OFF (default):
// no class is added and the page is byte-identical to today. classList.add is
// additive — it never clobbers other body classes.
if (INK_TOKEN_SWEEP_ENABLED && typeof document !== 'undefined' && document.body) {
  document.body.classList.add('ink-token-sweep');
}

// WEB-013 (WL-068): env-gated DSN + sampling, PII scrubbing via beforeSend, and
// masked Session Replay. Config logic lives in utils/sentryConfig (unit-tested);
// index.js just injects the Sentry integrations and initialises when enabled.
const sentryOptions = buildSentryOptions(process.env, {
  BrowserTracing,
  Replay: Sentry.Replay,
});
if (sentryOptions) {
  Sentry.init(sentryOptions);
}

if ('serviceWorker' in navigator) {
  // Add a small delay to ensure the app loads first
  setTimeout(() => {
    navigator.serviceWorker
      .register('/firebase-messaging-sw.js')
      .then((registration) => {
        console.log('✅ Service Worker registered:', registration);
      })
      .catch((error) => {
        console.warn('⚠️ Service Worker registration failed (this is normal in some browsers):', error);
        // Don't throw error - just log it as this shouldn't block the app
      });
  }, 1000);
}
// PRE-REACT BOOT LOADER (public/index.html, generated by scripts/gen-boot-loader.mjs).
// It is markup that paints with the HTML, covering the multi-second window before
// this bundle has downloaded/parsed/executed (MEASURED on web-stg with a warm
// cache: #root stayed empty for 4149 ms on /builder and 5704 ms on /wheel).
// Its teardown is DEFERRED past React's first paint (scheduleBootLoaderRemoval,
// double-rAF) — see bootLoaderTeardown.js. Removing it synchronously BEFORE
// render() (as we used to) exposed an intermediate empty #root that the browser
// painted one frame early → a 1-frame #f6f7f9 seam flash (measured bundle
// main.2f68817b.js, 2026-08-25). React 18's createRoot clears #root on its first
// commit — the same commit that paints the BrandWheelLoader Suspense fallback —
// so the boot loader and the React loader swap in ONE paint.

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  // S32: the fallback was a bare string, so a crash painted unstyled text with no
  // branding and no way out. AppCrashFallback is a tokenised, dependency-light
  // screen with a Reload action (see its header for why it imports so little).
  <Sentry.ErrorBoundary fallback={<AppCrashFallback />}>
    <App />
  </Sentry.ErrorBoundary>
);

// Boot loader teardown runs AFTER render(), deferred past React's first commit+
// paint via double-rAF, so it can never precede the React loader → no seam flash.
scheduleBootLoaderRemoval();

reportWebVitals();
